import { ChangeDetectionStrategy, Component, inject, signal, input, output, effect, computed } from '@angular/core';
import { CurrencyPipe, NgTemplateOutlet } from '@angular/common';

import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';

import { DateService } from '@core/services/date.service';
import { BookingTypeService } from '@core/services/bookingType.service';
import { CalenderService } from '@features/booking/calender/calender.service';
import { BookingPeriodExtended } from '@features/booking/calender/model/models';
import { BookingPeriod } from '@shared/models/bookingPeriod';
import { BookingMode } from '@features/booking/calender/model/enums';
import { BookingStatus } from '@shared/models/enums';
import {
  addCalendarDays,
  addCalendarMonths,
  apiDateTimeOf,
  diffCalendarDays,
  CalendarDay,
  timeOfDayFromApi,
  toCalendarDay,
} from '@shared/models/dateTypes';
import { closedText } from '@shared/components/abook-school-calendar/school-calendar.utils';
import { BookingRuleStatus } from '@features/booking/calender/model/bookingRuleStatus';
import { rangesOverlap } from '@shared/utilities/date-range.util';
import { REPEAT_INTERVALS, RepeatInterval, repeatDays } from './repeat-days.util';
import { readRepeatChoice, rememberRepeatChoice } from './repeat-choice';
import { mapLimited } from '@shared/utilities/map-limited';
import { AbookButton } from '@shared/components/abook-button/abook-button';
import { DanishDatePipe } from '@shared/utilities/danish-date.pipe';
import { AbookDateChipComponent, AbookFilterSelectComponent, FilterOption } from '@shared/components/filter';
import { CheckoutCardComponent } from '@features/checkout';
import { formatQuotaMinutes, quotaKey } from '@features/associations/components/basket/max-booking-time';

/** A quota as the series' badge says it: "maks. 4 timer pr. uge". */
const quotaText = (minutes: number, period: string) => `maks. ${formatQuotaMinutes(minutes)} pr. ${period}`;

/**
 * The selected dates that, counted together in date order, pass a maximum booking time the user may
 * not book past (#1138) — each date's own check counts only what is already booked, so a series
 * of dates each under the quota can be over it together. Grouped by quota and window, as the basket
 * adds up its lines (`blockingBasketMaxBookingTime`).
 */
export function overQuotaDates(periods: readonly BookingPeriodExtended[]): Set<BookingPeriodExtended> {
  return sumQuotas(periods, false);
}

/**
 * The series in date order against each quota (`quotaKey`, as the basket groups): the dates that pass
 * it. With `leaveOut`, such a date is deselected and marked `overQuota` and its minutes not counted,
 * so the ones after it may still fit — the search's first choice. The time being repeated counts
 * too — it is the series' first date — but is never itself over.
 */
function sumQuotas(periods: readonly BookingPeriodExtended[], leaveOut: boolean): Set<BookingPeriodExtended> {
  const used = new Map<string, number>();
  const over = new Set<BookingPeriodExtended>();
  const inSeries = periods.filter((p) => p.isSelected || p.isSource);
  for (const period of inSeries.sort((a, b) => a.start!.localeCompare(b.start!))) {
    const quota = period.maxBookingTime;
    if (!quota || quota.canOverride) continue;
    const key = quotaKey(quota);
    const total = (used.get(key) ?? quota.usedMinutes) + quota.requestedMinutes;
    if (total > quota.maxMinutes && !period.isSource) {
      over.add(period);
      if (leaveOut) {
        period.isSelected = false;
        period.overQuota = true;
        continue;
      }
    }
    used.set(key, total);
  }
  return over;
}

/** Leaves out, in date order, each selected date that would pass a quota with the ones before it. */
export function leaveOutOverQuota(periods: readonly BookingPeriodExtended[]): void {
  sumQuotas(periods, true);
}

/** How many dates the repeat search checks with the API at a time — the browser's connections per host. */
const REPEAT_CHECKS_AT_ONCE = 6;

/** The weekdays as the Ugedag chip lists them, Monday first; the values are JS getDay(). */
const WEEKDAY_OPTIONS: readonly FilterOption[] = [
  { value: 1, label: 'Mandag' },
  { value: 2, label: 'Tirsdag' },
  { value: 3, label: 'Onsdag' },
  { value: 4, label: 'Torsdag' },
  { value: 5, label: 'Fredag' },
  { value: 6, label: 'Lørdag' },
  { value: 0, label: 'Søndag' },
];

/**
 * Pure Business Logic Component for Repeated Booking
 *
 * Responsibilities:
 * - Manage form for repeated booking selection (weekdays, date range)
 * - Search for available booking periods based on criteria
 * - Emit selected periods or close signal
 *
 * Does NOT handle:
 * - Presentation concerns (dialog/drawer - adapters decide)
 * - Data format conversion (adapters handle this)
 * - API persistence (adapters handle this)
 * - Mode detection or conditional behavior (stateless)
 */
@Component({
  selector: 'app-repeated-booking',
  templateUrl: './repeated-booking.component.html',
  styleUrls: ['./repeated-booking.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DanishDatePipe,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatListModule,
    AbookButton,
    CurrencyPipe,
    AbookFilterSelectComponent,
    AbookDateChipComponent,
    CheckoutCardComponent,
    NgTemplateOutlet,
    MatIconModule,
  ],
})
export class RepeatedBookingComponent {
  // Inputs (required)
  selectedPeriod = input.required<BookingPeriod>();
  currentPeriods = input.required<BookingPeriod[]>();
  userId = input.required<number>();
  isSubmitting = input(false); // Loading state from adapter
  allowSelectPeriodConflicts = input(false); // Optional input to allow selecting periods with conflicts
  /**
   * Show each date's booker-type warning (#949). For the desk only: the text describes the time for
   * every booker type, which tells an association booking through its own basket nothing it can use.
   */
  showBookerTypeWarnings = input(false);
  /**
   * "Ingen pris fundet" on a date without a price. Off for a request, which has no price until the
   * administration approves it.
   */
  showMissingPrice = input(true);
  /**
   * `chips` (the association basket, #1138): Ugedag and "Gentag til" as filter chips after the
   * design system — the repeats always start from the period's own day, so only the last day is
   * chosen. `fields` (the desk): the weekday select and the from–to range as before.
   */
  variant = input<'fields' | 'chips'>('fields');
  protected readonly weekdayOptions = WEEKDAY_OPTIONS;
  protected readonly intervalOptions = REPEAT_INTERVALS;
  /** The form's value as a signal, for the chips. */
  protected readonly formValue = signal<{
    /** Null after `handleClose()` resets the form. */
    weekday: number[] | null;
    interval: RepeatInterval | null;
    start: CalendarDay | null;
    end: CalendarDay | null;
  }>({ weekday: [], interval: 'w1', start: null, end: null });

  // Outputs
  closed = output<void>();
  periodsSelected = output<BookingPeriodExtended[]>();

  // Dependencies
  private readonly formBuilder = inject(FormBuilder);
  private readonly calenderService = inject(CalenderService);
  private readonly bookingTypeService = inject(BookingTypeService);
  private readonly dateService = inject(DateService);

  // State signals
  modalFormRepeatedBooking = signal<FormGroup<any> | null>(null);
  searchResults = signal<BookingPeriodExtended[]>([]);
  isBooking = signal(true);
  isSearching = signal(false);
  searchButtonType = signal<'filled' | 'outlined'>('filled');

  constructor() {
    // Initialize form with validation
    const form = this.formBuilder.group({
      weekday: [[] as number[], Validators.required],
      interval: ['w1' as RepeatInterval, Validators.required],
      start: [null as CalendarDay | null, Validators.required],
      end: [null as CalendarDay | null, Validators.required],
    });
    this.modalFormRepeatedBooking.set(form);
    form.valueChanges.subscribe(() => {
      this.formValue.set(form.getRawValue());
      // The chips: a changed choice takes the dates found away, so "Find datoer" is offered again.
      if (this.variant() === 'chips') this.searchResults.set([]);
    });

    // Track form changes to update button appearance
    effect(() => {
      const form = this.modalFormRepeatedBooking();
      if (form) {
        form.valueChanges.subscribe(() => {
          // Set button to filled when form is modified
          this.searchButtonType.set('filled');
        });
      }
    });
    effect(async () => {
      this.selectedPeriod(); // Track changes to selectedPeriod input
      this.currentPeriods(); // Track changes to currentPeriods input

      // Initialize form with period data
      await this.initializePeriodData();
    });
  }

  // Add after the searchResults signal (around line 75)
  hasSelectedPeriods = computed(() => this.selectedCount() > 0);
  /** The selected dates past a quota together with the series' earlier ones (see {@link overQuotaDates}). */
  protected readonly overQuota = computed(() => overQuotaDates(this.searchResults()));
  protected readonly quotaText = quotaText;

  /** "Læg 3 tider i kurv". */
  protected readonly selectedCount = computed(
    () => this.searchResults().filter((period) => period.isSelected).length,
  );

  /**
   * The basket's dates in two groups (#1138): those that can go in — the time being repeated among
   * them, ticked and greyed — and those that cannot, with why. Each group folds open on its own.
   */
  protected readonly freePeriods = computed(() =>
    this.searchResults().filter((period) => period.isSource || !this.isPeriodDisabled(period)),
  );
  protected readonly takenPeriods = computed(() =>
    this.searchResults().filter((period) => !period.isSource && this.isPeriodDisabled(period)),
  );
  /**
   * The free dates to choose from — not the time being repeated, which is in the basket already — so
   * the header and "Læg n tider i kurv" say the same number when all are chosen.
   */
  private readonly choosable = computed(() => this.freePeriods().filter((period) => !period.isSource));
  protected readonly choosableCount = computed(() => this.choosable().length);
  protected readonly showFree = signal(false);
  protected readonly showTaken = signal(false);

  /** "Alle er valgt", "12 af 16 er valgt", "Ingen er valgt" — the time being repeated not counted. */
  protected readonly freeSummary = computed(() => {
    const choosable = this.choosable();
    const chosen = choosable.filter((period) => period.isSelected).length;
    if (choosable.length === 0) return 'Kun den valgte tid';
    if (chosen === choosable.length) return 'Alle er valgt';
    return chosen === 0 ? 'Ingen er valgt' : `${chosen} af ${choosable.length} er valgt`;
  });

  /** Why a date cannot go in, shown under it: the check's own words when it gives them. */
  protected takenReason(period: BookingPeriodExtended): string {
    // A closing or holiday by its name, as the skolekalender writes it.
    if (period.closedLabel) return closedText(period.closedLabel);
    if (period.message) return period.message;
    if (this.showBookerTypeWarnings() && period.bookerTypeWarning) return period.bookerTypeWarning;
    return period.isDisabled ? 'Overlapper en eksisterende periode' : 'Tiden er ikke ledig';
  }

  isPeriodDisabled(period: BookingPeriodExtended): boolean {
    if (this.allowSelectPeriodConflicts()) {
      return !!period.isDisabled; // Allow selection of conflicting periods
    }
    return period.isDisabled || !period.isAvailable; // Default to disabled if not explicitly allowed
  }

  /**
   * Handle selection changes on the periods list
   */
  onPeriodsSelectionChange(event: MatSelectionListChange): void {
    event.options.forEach((option) => {
      const selectedPeriod = option.value as BookingPeriodExtended;
      selectedPeriod.isSelected = option.selected;
    });

    // Notify the signal that searchResults changed
    this.searchResults.set([...this.searchResults()]);
  }

  /**
   * Initialize form with selected period data
   */
  private async initializePeriodData(): Promise<void> {
    const period = this.selectedPeriod();
    if (!period) return;

    const form = this.modalFormRepeatedBooking();
    if (!form) return;

    // Both controls are datepickers, so they hold calendar days.
    const periodStart = toCalendarDay(period.start!);

    // The basket starts from the last "Gentag" and "Gentag til" (#1138); the weekday is the time's own.
    const remembered = this.variant() === 'chips' ? readRepeatChoice(periodStart) : null;
    form.patchValue({
      weekday: [this.dateService.getDayOfWeek(this.dateService.fromApiString(period.start!))],
      interval: remembered?.interval ?? 'w1',
      start: periodStart,
      end: remembered?.end ?? addCalendarMonths(periodStart, 1),
    });

    // Determine if period is a booking or not
    if (period.bookingTypeId) {
      const bookingTypes = await firstValueFrom(this.bookingTypeService.getList());
      const bookingType = bookingTypes.find((x) => x.id === period.bookingTypeId);
      this.isBooking.set(bookingType?.bookingMode === BookingMode.BOOKING);
    } else {
      this.isBooking.set(true);
    }
  }

  /**
   * Search for available periods matching the selected criteria
   */
  async onSearchAvailableDates(): Promise<void> {
    const period = this.selectedPeriod();
    if (this.isSearching() || !period) return;

    this.isSearching.set(true);

    const form = this.modalFormRepeatedBooking();
    if (!form) {
      this.isSearching.set(false);

      return;
    }

    const formValues = form.getRawValue();

    // The clock readings the repeats copy, taken straight off the source period's strings — going
    // via a Date would lose a reading that falls in the spring-forward gap.
    const startTime = timeOfDayFromApi(period.start!);
    const endTime = timeOfDayFromApi(period.end!);

    // How many days the source period spans, so each generated repeat gets the same shape.
    const dayOffset = diffCalendarDays(toCalendarDay(period.end!), toCalendarDay(period.start!));

    const existingPeriods = this.currentPeriods().map((x) => ({
      start: this.dateService.fromApiString(x.start!),
      end: this.dateService.fromApiString(x.end!),
    }));

    // The datepickers hand back calendar days, so choosing the days is pure calendar arithmetic.
    const days = repeatDays(formValues.start, formValues.end, formValues.weekday, formValues.interval ?? 'w1');

    // Each date is checked with the API — six at a time, not one after another: half a year of
    // Saturdays took 11 s in turn (~0.4 s a check). The list keeps the dates' order.
    const checked = await mapLimited(days, REPEAT_CHECKS_AT_ONCE, async (currDay) => {
      const periodStart = apiDateTimeOf(currDay, startTime);
      const periodEnd = apiDateTimeOf(addCalendarDays(currDay, dayOffset), endTime);

      try {
        // Check availability from API
        const periodResponse = await firstValueFrom(
          this.calenderService.checkAvailability(
            period.resourceId!,
            periodStart,
            periodEnd,
            period.bookingId!,
            this.userId(),
            period.activityId!,
            true, // allowPartialCoverage — a gapped generated period should sum what's priced, not default to 0. See #336.
          ),
        );

        const newStart = this.dateService.fromApiString(periodStart);
        const newEnd = this.dateService.fromApiString(periodEnd);
        const overlapsExisting = existingPeriods.some((x) => rangesOverlap(newStart, newEnd, x.start, x.end));
        // The period itself, already in the basket or booking: the source, not an overlap with itself.
        const isSource = overlapsExisting && periodStart === period.start && periodEnd === period.end;
        const hasOverlap = overlapsExisting && !isSource;

        // Create extended period with metadata
        return this.createAvailablePeriod(periodStart, periodEnd, periodResponse, hasOverlap, isSource);
      } catch (error) {
        console.error('Error checking period availability:', error);
        return null;
      }
    });

    const found = checked.filter((period): period is BookingPeriodExtended => period !== null);
    // The series counts against the quota together: in date order, a date that would pass it is
    // left out, so the ones after it may still fit.
    leaveOutOverQuota(found);

    // Update signals
    this.searchResults.set(found);

    this.isSearching.set(false);

    this.searchButtonType.set('outlined');
  }

  /**
   * Create an extended booking period with availability metadata
   */
  private createAvailablePeriod(
    periodStart: string,
    periodEnd: string,
    periodResponse: any,
    hasOverlap: boolean,
    isSource = false,
  ): BookingPeriodExtended {
    const availablePeriod = JSON.parse(JSON.stringify(this.selectedPeriod())) as any;
    availablePeriod.start = periodStart;
    availablePeriod.end = periodEnd;
    availablePeriod.message = periodResponse.message;
    // The booker types the date is restricted for (#949): the server's text, as the fixed drawer and
    // the calendar's timeslots show it. Absent from an API from before the field.
    availablePeriod.bookerTypeWarning = periodResponse.bookerTypeWarning ?? null;
    availablePeriod.maxBookingTime = periodResponse.maxBookingTime ?? null;
    availablePeriod.closedLabel =
      (periodResponse.bookingRules as BookingRuleStatus[] | undefined)?.find((rule) => rule.closedLabel)
        ?.closedLabel ?? null;
    availablePeriod.statusId = BookingStatus.New;
    availablePeriod.completedOn = null;
    availablePeriod.deletedOn = null;

    if (this.isBooking()) {
      availablePeriod.price = periodResponse.price ?? 0;
    }

    availablePeriod.isAvailable = periodResponse.isAvailable;
    availablePeriod.isSource = isSource;
    availablePeriod.isDisabled = hasOverlap || isSource;
    availablePeriod.isSelected =
      hasOverlap || isSource ? false : periodResponse.isAvailable ? true : periodResponse.canOverride;

    return availablePeriod as BookingPeriodExtended;
  }

  protected setWeekdays(value: number | number[] | null): void {
    this.modalFormRepeatedBooking()?.patchValue({
      weekday: Array.isArray(value) ? value : value === null ? [] : [value],
    });
  }

  protected setInterval(value: string | string[] | null): void {
    if (typeof value === 'string') this.modalFormRepeatedBooking()?.patchValue({ interval: value });
    this.rememberChoice();
  }

  protected setEnd(day: CalendarDay): void {
    this.modalFormRepeatedBooking()?.patchValue({ end: day });
    this.rememberChoice();
  }

  /** What the basket's chips now say, for the next time it repeats a time. */
  private rememberChoice(): void {
    const form = this.modalFormRepeatedBooking();
    if (this.variant() !== 'chips' || !form) return;
    const { interval, end } = form.getRawValue();
    rememberRepeatChoice({ interval, end });
  }

  /**
   * Submit selected periods (adapters decide what to do)
   */
  onSubmit(): void {
    const selectedPeriods = this.searchResults().filter((period) => period.isSelected);

    if (selectedPeriods.length > 0) {
      this.periodsSelected.emit(selectedPeriods);
    }
  }

  /**
   * Reset state and emit close signal
   * Adapters decide what happens next (close dialog/drawer)
   */
  handleClose(): void {
    const form = this.modalFormRepeatedBooking();
    if (form) {
      form.reset();
    }
    this.searchResults.set([]);
    this.closed.emit();
  }
}
